import { useState, useMemo, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import classNames from 'classnames';

import { CloseButton } from './CloseButton';

export function Tooltip({
  tooltipContent,
  children,
  placement = 'top',
  offset = 10,
  skiddingPercent = 50,
  arrowSkiddingPercent = 50,
  interactive = false,
  hover = true,
  show = true,
  timeout = null,
  onDismiss = null,
  style = {},
  containerStyle = {},
  container = 'body',
}) {
  useEffect(() => {
    let timer;
    if (timeout && onDismiss) {
      timer = setTimeout(onDismiss, timeout);
    } else if (timeout) {
      timer = setTimeout(() => {
        setVisible(false);
      }, timeout);
    }
    return () => clearTimeout(timer);
  }, [timeout, onDismiss]);

  useEffect(() => {
    if (!show && hover) setVisible(false);
  }, [show, hover]);

  const tooltipId = useId();

  const [visible, setVisible] = useState(!hover);
  const [referenceElement, setReferenceElement] = useState(null);
  const [tooltipElement, setTooltipElement] = useState(null);
  const {
    styles: popperStyles,
    attributes,
    state,
  } = usePopper(referenceElement, tooltipElement, {
    placement: placement,
    modifiers: [
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: [placement],
          fallbackPlacements: [placement],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
        },
      },
      useMemo(
        () => ({
          name: 'offset',
          options: {
            offset: ({ popper, reference, placement }) => {
              if (placement === 'top' || placement === 'bottom') {
                return [
                  popper.width * ((50 - arrowSkiddingPercent) / 100) - reference.width * ((50 - skiddingPercent) / 100),
                  0,
                ];
              } else {
                return [
                  popper.height * ((50 - arrowSkiddingPercent) / 100) -
                    reference.height * ((50 - skiddingPercent) / 100),
                  0,
                ];
              }
            },
          },
        }),
        [arrowSkiddingPercent, skiddingPercent],
      ),
    ],
  });

  let offsetStyle = {};
  let arrowStyle = {};

  const popperPlacement = attributes.popper?.['data-popper-placement'];
  const isTop = popperPlacement?.match(/^top/);
  const isBottom = popperPlacement?.match(/^bottom/);
  const isLeft = popperPlacement?.match(/^left/);
  const isRight = popperPlacement?.match(/^right/);

  if (isTop) {
    offsetStyle = { paddingBottom: `${offset}px` };
    arrowStyle = {
      left: `calc(${arrowSkiddingPercent}% - ${state?.modifiersData?.preventOverflow?.x ?? 0}px)`,
    };
  } else if (isBottom) {
    offsetStyle = { paddingTop: `${offset}px` };
    arrowStyle = {
      left: `calc(${arrowSkiddingPercent}% - ${state?.modifiersData?.preventOverflow?.x ?? 0}px)`,
    };
  } else if (isLeft) {
    offsetStyle = { paddingRight: `${offset}px` };
    arrowStyle = {
      top: `calc(${arrowSkiddingPercent}% - ${state?.modifiersData?.preventOverflow?.y ?? 0}px)`,
    };
  } else if (isRight) {
    offsetStyle = { paddingLeft: `${offset}px` };
    arrowStyle = {
      top: `calc(${arrowSkiddingPercent}% - ${state?.modifiersData?.preventOverflow?.y ?? 0}px)`,
    };
  }

  function portal() {
    return createPortal(
      <div
        id={tooltipId}
        ref={setTooltipElement}
        className="z-50 bg-transparent"
        style={{ ...popperStyles.popper, ...offsetStyle, ...containerStyle }}
        {...attributes.popper}
        role="tooltip"
      >
        <div
          className={classNames(
            'relative flex flex-col rounded text-center text-xs drop-shadow-md',
            'bg-gray-900 text-white',
            {
              'pb-4 pl-4 pr-6 pt-6': onDismiss,
              'px-2 py-1.5': !onDismiss,
            },
          )}
          style={{ ...style }}
          {...attributes.popper}
          data-testid="tooltip"
        >
          {/* arrow */}
          <div
            className={classNames('absolute h-0 w-0 border-5 border-transparent', {
              'top-full -translate-x-1/2 border-t-gray-900': isTop,
              'bottom-full -translate-x-1/2 border-b-gray-900': isBottom,
              'left-full -translate-y-1/2 border-l-gray-900': isLeft,
              'right-full -translate-y-1/2 border-r-gray-900': isRight,
            })}
            style={{ ...arrowStyle }}
          ></div>

          {onDismiss && (
            <CloseButton
              onClick={onDismiss}
              label="close tooltip"
              buttonClass="!top-3 !right-3 !w-3.5 !h-3.5"
              iconClass="!w-2.5 !h-2.5"
            />
          )}
          {tooltipContent}
        </div>
      </div>,
      document.querySelector(container) || document.body,
    );
  }

  if (!show) return children;

  return (
    <>
      <div
        className="relative min-w-0"
        ref={setReferenceElement}
        onMouseEnter={() => {
          hover && setVisible(true);
        }}
        onPointerEnter={() => {
          hover && setVisible(true);
        }}
        onMouseLeave={() => {
          hover && setVisible(false);
        }}
        onPointerLeave={() => {
          hover && setVisible(false);
        }}
        aria-describedby={tooltipId}
      >
        {children}
        {visible && interactive && portal()}
      </div>
      {visible && !interactive && portal()}
    </>
  );
}
